/**
 * ControllerDebug component provides a debug panel for inspecting controller input events.
 * Displays raw and semantic input events, device information, and controller profiles for debugging.
 */
import * as React from 'react'
import { useFocusNavigationDep, useControllerInputDep } from '../../../providers/FocusNavigationProvider/FocusNavigationProvider'
import { useSignals } from '@preact/signals-react/runtime'
import type { ControllerInputEvent, RawInputEventInfo } from '@merels/input/controllerInput'

interface GamepadInfo {
  /** Short identifier (X1, X2, etc.) */
  id: string
  /** Gamepad index */
  index: number
  /** Gamepad model/type ID */
  modelId: string
  /** When this controller was first connected */
  connectedAt: number
}

interface RawInputEvent {
  /** Which input device (X1, X2, K, etc.) */
  deviceId: string
  /** Raw input description (e.g., "A button press", "ArrowUp key") */
  rawEvent: string
  /** Gamepad index (if applicable) */
  gamepadIndex?: number
  /** Button index (if applicable) */
  buttonIndex?: number
  /** Axis values (if applicable) */
  axes?: number[]
  /** Timestamp for matching with semantic events */
  timestamp: number
}

interface InputEventLog {
  id: number
  timestamp: number
  /** Device that sent the event (X1, X2, K, etc.) */
  deviceId: string
  /** Raw input event */
  rawEvent: string
  /** Semantic event it was mapped to */
  semanticEvent: ControllerInputEvent
}

export function ControllerDebug() {
  const [events, setEvents] = React.useState<InputEventLog[]>([])
  const [gamepads, setGamepads] = React.useState<Map<number, GamepadInfo>>(new Map())
  const [expandedGamepad, setExpandedGamepad] = React.useState<number | null>(null)
  const eventIdRef = React.useRef(0)
  const maxEvents = 100 // Keep last 100 events
  const rawEventQueueRef = React.useRef<RawInputEvent[]>([]) // Queue of raw events waiting to be matched
  useSignals()
  const focusNavigation = useFocusNavigationDep()
  const controllerInput = useControllerInputDep()
  const isFocusNavActive = focusNavigation.isActive.value
  const focusArea = focusNavigation.focusArea.value

  // Track gamepads by index (X1, X2, etc.)
  const gamepadsRef = React.useRef<Map<number, GamepadInfo>>(new Map())
  
  React.useEffect(() => {
    const updateGamepads = () => {
      const gamepadList = Array.from(navigator.getGamepads()).filter((gp): gp is Gamepad => gp !== null)
      const now = Date.now()
      const newGamepads = new Map<number, GamepadInfo>()

      for (const gamepad of gamepadList) {
        if (!gamepadsRef.current.has(gamepad.index)) {
          // New gamepad - assign ID based on index (X1, X2, etc.)
          const id = `X${gamepad.index + 1}`
          newGamepads.set(gamepad.index, {
            id,
            index: gamepad.index,
            modelId: gamepad.id || 'Unknown',
            connectedAt: now,
          })
          console.log(`New gamepad detected: ${id} (${gamepad.id})`)
        } else {
          // Existing gamepad
          newGamepads.set(gamepad.index, gamepadsRef.current.get(gamepad.index)!)
        }
      }

      gamepadsRef.current = newGamepads
      setGamepads(new Map(newGamepads))
    }

    const handleConnected = (e: GamepadEvent) => {
      console.log('Gamepad connected event:', e.gamepad.id, 'index:', e.gamepad.index)
      updateGamepads()
    }

    const handleDisconnected = (e: GamepadEvent) => {
      console.log('Gamepad disconnected event:', e.gamepad.id, 'index:', e.gamepad.index)
      // Note: The gamepad is already removed from navigator.getGamepads() when this fires
      // So we'll clean it up in the next updateGamepads call
      updateGamepads()
    }

    updateGamepads()
    window.addEventListener('gamepadconnected', handleConnected)
    window.addEventListener('gamepaddisconnected', handleDisconnected)

    // Poll for gamepad updates
    const interval = setInterval(updateGamepads, 100)

    return () => {
      window.removeEventListener('gamepadconnected', handleConnected)
      window.removeEventListener('gamepaddisconnected', handleDisconnected)
      clearInterval(interval)
    }
  }, [])

  // Subscribe to raw input events from the input context
  React.useEffect(() => {
    const handleRawEvent = (rawEvent: RawInputEventInfo) => {
      rawEventQueueRef.current.push({
        deviceId: rawEvent.deviceId,
        rawEvent: rawEvent.rawEvent,
        timestamp: rawEvent.timestamp,
      })
      // Keep only recent events (last 1 second)
      rawEventQueueRef.current = rawEventQueueRef.current.filter(
        ev => Date.now() - ev.timestamp < 1000
      )
    }

    return controllerInput.subscribeRaw(handleRawEvent)
  }, [controllerInput])

  // Track raw gamepad events
  React.useEffect(() => {
    const previousButtonStates = new Map<number, boolean[]>()
    const previousAxisStates = new Map<number, number[][]>()
    const STICK_DEADZONE = 0.3

    const pollGamepads = () => {
      const gamepadList = Array.from(navigator.getGamepads()).filter((gp): gp is Gamepad => gp !== null)
      
      for (const gamepad of gamepadList) {
        if (!gamepad.connected) continue
        
        // Get or create gamepad info
        let gamepadInfo = gamepadsRef.current.get(gamepad.index)
        if (!gamepadInfo) {
          // Gamepad not tracked yet - create entry on the fly
          const id = `X${gamepad.index + 1}`
          gamepadInfo = {
            id,
            index: gamepad.index,
            modelId: gamepad.id || 'Unknown',
            connectedAt: Date.now(),
          }
          gamepadsRef.current.set(gamepad.index, gamepadInfo)
          setGamepads(new Map(gamepadsRef.current))
        }

        const prevButtons = previousButtonStates.get(gamepad.index) || []
        const prevAxes = previousAxisStates.get(gamepad.index) || [[0, 0]]

        // Check button presses
        const buttonNames: Record<number, string> = {
          0: 'A button',
          1: 'B button',
          2: 'X button',
          3: 'Y button',
          12: 'D-pad Up',
          13: 'D-pad Down',
          14: 'D-pad Left',
          15: 'D-pad Right',
        }

        for (let i = 0; i < gamepad.buttons.length; i++) {
          if (gamepad.buttons[i]?.pressed && !prevButtons[i]) {
            const buttonName = buttonNames[i] || `Button ${i}`
            rawEventQueueRef.current.push({
              deviceId: gamepadInfo.id,
              rawEvent: `${buttonName} press`,
              gamepadIndex: gamepad.index,
              buttonIndex: i,
              timestamp: Date.now(),
            })
          }
        }

        // Check stick movement (edge detection)
        const leftStickX = gamepad.axes[0] || 0
        const leftStickY = gamepad.axes[1] || 0
        const prevStickX = prevAxes[0]?.[0] || 0
        const prevStickY = prevAxes[0]?.[1] || 0

        const wasInDeadzone = Math.abs(prevStickX) < STICK_DEADZONE && Math.abs(prevStickY) < STICK_DEADZONE
        const isInDeadzone = Math.abs(leftStickX) < STICK_DEADZONE && Math.abs(leftStickY) < STICK_DEADZONE

        if (!isInDeadzone && wasInDeadzone) {
          let direction = ''
          if (Math.abs(leftStickX) > Math.abs(leftStickY)) {
            direction = leftStickX < 0 ? 'Left' : 'Right'
          } else {
            direction = leftStickY < 0 ? 'Up' : 'Down'
          }
          rawEventQueueRef.current.push({
            deviceId: gamepadInfo.id,
            rawEvent: `Left stick ${direction}`,
            gamepadIndex: gamepad.index,
            axes: [leftStickX, leftStickY],
            timestamp: Date.now(),
          })
        }

        previousButtonStates.set(gamepad.index, gamepad.buttons.map(btn => btn.pressed))
        previousAxisStates.set(gamepad.index, [[leftStickX, leftStickY]])
      }

      // Keep only recent events (last 1 second)
      rawEventQueueRef.current = rawEventQueueRef.current.filter(
        ev => Date.now() - ev.timestamp < 1000
      )
    }

    const interval = setInterval(pollGamepads, 16)
    return () => clearInterval(interval)
  }, [])

  // Subscribe to semantic events and match with raw events
  React.useEffect(() => {
    const unsubscribe = controllerInput.subscribe((semanticEvent: any) => {
      const now = Date.now()
    
    // Try to find matching raw event (within 300ms window - wider to account for processing delays)
    let rawEvent: RawInputEvent | null = null
    const matchWindow = 300 // ms

    // Look for the most recent matching event
    for (let i = rawEventQueueRef.current.length - 1; i >= 0; i--) {
      const raw = rawEventQueueRef.current[i]
      const timeDiff = now - raw.timestamp
      
      if (timeDiff >= 0 && timeDiff < matchWindow) {
        // Check if the raw event could map to this semantic event
        const couldMatch = 
          (semanticEvent.type === 'navigate' && (raw.rawEvent.includes('D-pad') || raw.rawEvent.includes('stick') || raw.rawEvent.includes('Arrow'))) ||
          (semanticEvent.type === 'select' && (raw.rawEvent.includes('A button') || raw.rawEvent.includes('Enter') || raw.rawEvent.includes('Space'))) ||
          (semanticEvent.type === 'exit' && (raw.rawEvent.includes('B button') || raw.rawEvent.includes('Escape'))) ||
          (semanticEvent.type === 'menu' && (raw.rawEvent.includes('Menu button') || raw.rawEvent.includes('M key')))

        if (couldMatch) {
          rawEvent = raw
          rawEventQueueRef.current.splice(i, 1) // Remove matched event
          break
        }
      }
    }

    // If still no match, try any recent keyboard event (within window) - prioritize keyboard
    if (!rawEvent) {
      for (let i = rawEventQueueRef.current.length - 1; i >= 0; i--) {
        const raw = rawEventQueueRef.current[i]
        const timeDiff = now - raw.timestamp
        if (timeDiff >= 0 && timeDiff < matchWindow && raw.deviceId === 'K') {
          rawEvent = raw
          rawEventQueueRef.current.splice(i, 1)
          break
        }
      }
    }

    // If still no match, try any recent event (within window)
    if (!rawEvent) {
      for (let i = rawEventQueueRef.current.length - 1; i >= 0; i--) {
        const raw = rawEventQueueRef.current[i]
        if (now - raw.timestamp < matchWindow) {
          rawEvent = raw
          rawEventQueueRef.current.splice(i, 1)
          break
        }
      }
    }

    // If no raw event found, it might be from a source we're not tracking
    // (e.g., a gamepad we haven't seen yet, or timing issue)
    if (!rawEvent) {
      // Try to infer from semantic event
      let inferredDevice = '?'
      let inferredRaw = 'Unknown input'
      
      // Check if any gamepad has buttons pressed (likely source)
      const gamepadList = Array.from(navigator.getGamepads()).filter((gp): gp is Gamepad => gp !== null)
      for (const gp of gamepadList) {
        if (gp.connected) {
          const hasPressed = gp.buttons.some(btn => btn.pressed)
          const hasActiveStick = Math.abs(gp.axes[0] || 0) > 0.3 || Math.abs(gp.axes[1] || 0) > 0.3
          if (hasPressed || hasActiveStick) {
            const info = gamepadsRef.current.get(gp.index)
            if (info) {
              inferredDevice = info.id
              // Try to determine which button/stick
              if (semanticEvent.type === 'select' && gp.buttons[0]?.pressed) {
                inferredRaw = 'A button press'
              } else if (semanticEvent.type === 'exit' && gp.buttons[1]?.pressed) {
                inferredRaw = 'B button press'
              } else if (semanticEvent.type === 'menu' && gp.buttons[9]?.pressed) {
                inferredRaw = 'Menu button press'
              } else if (semanticEvent.type === 'navigate') {
                if (gp.buttons[12]?.pressed) inferredRaw = 'D-pad Up press'
                else if (gp.buttons[13]?.pressed) inferredRaw = 'D-pad Down press'
                else if (gp.buttons[14]?.pressed) inferredRaw = 'D-pad Left press'
                else if (gp.buttons[15]?.pressed) inferredRaw = 'D-pad Right press'
                else if (Math.abs(gp.axes[0] || 0) > 0.3 || Math.abs(gp.axes[1] || 0) > 0.3) {
                  const dir = semanticEvent.type === 'navigate' ? semanticEvent.direction : ''
                  inferredRaw = `Left stick ${dir}`
                }
              }
              break
            }
          }
        }
      }
      
      rawEvent = {
        deviceId: inferredDevice,
        rawEvent: inferredRaw,
        timestamp: now,
      }
    }

    const logEntry: InputEventLog = {
      id: eventIdRef.current++,
      timestamp: now,
      deviceId: rawEvent.deviceId,
      rawEvent: rawEvent.rawEvent,
      semanticEvent,
    }

      setEvents((prev) => {
        const newEvents = [logEntry, ...prev]
        return newEvents.slice(0, maxEvents)
      })
    })
    return unsubscribe
  }, [controllerInput])

  const formatSemanticEvent = (event: ControllerInputEvent): string => {
    switch (event.type) {
      case 'navigate':
        return `Navigate ${event.direction}`
      case 'select':
        return 'Select'
      case 'configure':
        return 'Configure'
      case 'cancel':
        return 'Cancel'
      case 'menu':
        return 'Toggle'
      case 'exit':
        return 'Exit'
      case 'togglePlayer':
        return 'Toggle Player'
    }
    return 'Unknown'
  }

  const formatTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp)
    return date.toLocaleTimeString() + '.' + date.getMilliseconds().toString().padStart(3, '0')
  }

  const clearEvents = () => {
    setEvents([])
    eventIdRef.current = 0
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex items-center justify-between p-2 border-b">
        <div className="flex items-center gap-3">
          <h3 className="font-semibold text-sm">Controller Debug</h3>
          <div className={`px-2 py-1 text-xs rounded font-medium ${
            isFocusNavActive
              ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border border-green-300 dark:border-green-700'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-700'
          }`}>
            Focus Nav: {isFocusNavActive ? 'ON' : 'OFF'}
            {isFocusNavActive && (
              <span className="ml-1 text-xs">({focusArea})</span>
            )}
          </div>
        </div>
        <button
          onClick={clearEvents}
          className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
        >
          Clear
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-4">
        {/* Gamepad Status */}
        <div>
          <h4 className="font-semibold text-xs mb-2">Connected Gamepads</h4>
          <div className="space-y-1">
            {/* Always show keyboard */}
            <div
              className="text-xs p-2 bg-gray-100 dark:bg-gray-800 rounded cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700"
              onClick={() => setExpandedGamepad(expandedGamepad === -1 ? null : -1)}
            >
              <div className="font-medium">K</div>
              {expandedGamepad === -1 && (
                <div className="text-gray-600 dark:text-gray-400 space-y-0.5 mt-1">
                  <div>Keyboard (simulated gamepad)</div>
                </div>
              )}
            </div>
            
            {/* Show connected gamepads */}
            {gamepads.size === 0 ? (
              <p className="text-xs text-gray-500 dark:text-gray-400 pl-2">No gamepads connected</p>
            ) : (
              Array.from(gamepads.values()).map((info) => {
                const gamepad = Array.from(navigator.getGamepads())
                  .find((gp): gp is Gamepad => gp !== null && gp.index === info.index)
                
                return (
                  <div
                    key={info.index}
                    className="text-xs p-2 bg-gray-100 dark:bg-gray-800 rounded cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700"
                    onClick={() => setExpandedGamepad(expandedGamepad === info.index ? null : info.index)}
                  >
                    <div className="font-medium">{info.id}</div>
                    {expandedGamepad === info.index && (
                      <div className="text-gray-600 dark:text-gray-400 space-y-0.5 mt-1">
                        <div>Model: {info.modelId}</div>
                        <div>Index: {info.index}</div>
                        {gamepad && (
                          <div>Buttons: {gamepad.buttons.length} | Axes: {gamepad.axes.length}</div>
                        )}
                        <div className="text-gray-500 dark:text-gray-500 text-xs mt-1">
                          Connected: {new Date(info.connectedAt).toLocaleTimeString()}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })
            )}
          </div>
        </div>

        {/* Event Log */}
        <div>
          <h4 className="font-semibold text-xs mb-2">Input Events ({events.length})</h4>
          {events.length === 0 ? (
            <p className="text-xs text-gray-500 dark:text-gray-400">No events yet. Enable focus navigation (F key) and use keyboard or controller.</p>
          ) : (
            <div className="space-y-1">
              {events.map((log) => (
                <div
                  key={log.id}
                  className={`text-xs p-2 rounded border ${
                    log.deviceId === 'K'
                      ? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                      : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium">{formatSemanticEvent(log.semanticEvent)}</span>
                    <span className="text-gray-500 dark:text-gray-400">{formatTimestamp(log.timestamp)}</span>
                  </div>
                  <div className="space-y-0.5 text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-2">
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                        log.deviceId === 'K'
                          ? 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                          : 'bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200'
                      }`}>
                        {log.deviceId}
                      </span>
                      <span className="text-xs">{log.rawEvent}</span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-500">
                      → {formatSemanticEvent(log.semanticEvent)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
